import { useEffect, useState } from 'react';
import { Accordion, Cell, List, Placeholder, Section, Switch } from '@telegram-apps/telegram-ui';
import {
  BoxOwnerView,
  BRAND_NAME,
  SUPPORTED_LOCALES,
  type Locale,
} from '@veylme/shared';

import { apiFetch, getNotifications, updateNotifications } from '../api/client.js';
import { humanizeApiError } from '../lib/apiError.js';
import { useSetLocale } from '../i18n/LocaleContext.js';
import { MyCodePanel } from '../components/MyCodePanel.js';
import { SkeletonRows } from '../components/Skeleton.js';
import { isPremiumUser } from '../lib/tg.js';
import { useT } from '../hooks/useT.js';
import { useLocale } from '../i18n/LocaleContext.js';
import { BoxPanel } from './BoxPanel.js';
import { InvitePanel } from './InvitePanel.js';
import styles from './SettingsScreen.module.css';

/** Версия приложения — синхронизирована с apps/miniapp/package.json. */
const APP_VERSION = '0.12.0';

type BoxState =
  | { kind: 'loading' }
  | { kind: 'loaded'; box: BoxOwnerView }
  | { kind: 'error' };

/**
 * Экран «Настройки» — вкладка нижней навигации (Tabbar).
 *
 * Веха 13.x: сюда переехал «Твой ящик» (BoxPanel) — тумблер приёма вопросов и
 * ссылка для приглашений. Вкладка «Вопросы» теперь только список вопросов.
 *
 * Ящик грузим idempotent-upsert'ом POST /boxes БЕЗ `ref`: реферальная атрибуция
 * происходит на ПЕРВОМ боксе, который всегда создаётся раньше — на mount'е
 * InboxScreen (стартовый экран). Настройки доступны только из Tabbar, т.е. уже
 * после Inbox, поэтому сюда ящик всегда приходит существующим.
 *
 * work-020: Cell «Спросить по коду» — постоянная точка входа в экран ввода кода
 * (универсально, для всех — решение 6). MyCodePanel (свой код автора + ротация) —
 * только для НЕ-Premium (треб. 1: Premium ничего про коды не видит).
 *
 * work-024 D (ВЛ-1): тумблер уведомлений теперь рабочий — GET /me/notifications
 * на mount, PATCH /me/notifications при переключении.
 *
 * work-036 (Трек D): раздел «Напиши мне» — панель шеринга message-ящика
 * (InvitePanel в lazy-режиме, `format="message"`: НЕ грузит бокс на mount,
 * запрос уходит только когда пользователь реально раскрывает аккордеон
 * ссылки, развилка Б — не плодим message-ящик у всех подряд) + вход в
 * «Полученные сообщения» (`onOpenMessages`, под-поток MESSAGES).
 */
export interface SettingsScreenProps {
  /** Открыть экран ввода кода (ASK_BY_CODE) — Cell «Спросить по коду». */
  onEnterCode: () => void;
  /** Является ли текущий пользователь администратором. */
  isAdmin: boolean;
  /** Открыть панель аналитики (ANALYTICS). */
  onOpenAnalytics: () => void;
  /** Открыть «Мои вопросы» (MY_QUESTIONS) — retention-answer-loop. */
  onOpenMyQuestions: () => void;
  /** Открыть «Полученные сообщения» (MESSAGES) — инбокс message-ящика. */
  onOpenMessages: () => void;
}

type NotificationsState =
  | { kind: 'loading' }
  | { kind: 'loaded'; enabled: boolean }
  | { kind: 'error' };

export function SettingsScreen({
  onEnterCode,
  isAdmin,
  onOpenAnalytics,
  onOpenMyQuestions,
  onOpenMessages,
}: SettingsScreenProps) {
  const t = useT();
  const locale = useLocale();
  const changeLocale = useSetLocale();
  const [notifState, setNotifState] = useState<NotificationsState>({ kind: 'loading' });
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifError, setNotifError] = useState<string | null>(null);
  const [localeError, setLocaleError] = useState<string | null>(null);
  const [localeSaving, setLocaleSaving] = useState(false);
  const [boxState, setBoxState] = useState<BoxState>({ kind: 'loading' });
  // Список языков длинный (27) — прячем под аккордеон, в шапке показываем
  // текущий выбранный. По умолчанию свёрнут (как нативный «Language» Telegram).
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const box = await apiFetch('/boxes', {
          method: 'POST',
          body: { format: 'ask' },
          parse: BoxOwnerView,
        });
        if (!cancelled) setBoxState({ kind: 'loaded', box });
      } catch {
        if (!cancelled) setBoxState({ kind: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { enabled } = await getNotifications();
        if (!cancelled) setNotifState({ kind: 'loaded', enabled });
      } catch {
        if (!cancelled) setNotifState({ kind: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleNotificationsToggle(checked: boolean) {
    // Оптимистичное обновление UI.
    setNotifState({ kind: 'loaded', enabled: checked });
    setNotifError(null);
    setNotifSaving(true);
    try {
      const { enabled } = await updateNotifications(checked);
      setNotifState({ kind: 'loaded', enabled });
    } catch (err: unknown) {
      // Откат — восстановить предыдущее значение.
      setNotifState({ kind: 'loaded', enabled: !checked });
      setNotifError(humanizeApiError(err, t) || t.settings_notifications_error);
    } finally {
      setNotifSaving(false);
    }
  }

  async function handleLocaleChange(next: Locale) {
    if (next === locale) return;
    setLocaleError(null);
    setLocaleSaving(true);
    try {
      await changeLocale(next);
      // Успех — сворачиваем аккордеон (как нативный «Language» Telegram: выбрал
      // язык — список закрылся). Закрываем ТОЛЬКО после успешного сохранения:
      // при ошибке аккордеон остаётся открытым, чтобы был виден localeError
      // (он рендерится Cell'ом внутри Accordion.Content).
      setLangOpen(false);
    } catch {
      setLocaleError(t.settings_language_error);
    } finally {
      setLocaleSaving(false);
    }
  }

  // Самоназвание текущего языка для шапки аккордеона (fallback на код, если
  // вдруг не нашли — не должно случаться: locale ∈ SUPPORTED_LOCALES).
  const currentLocaleNative =
    SUPPORTED_LOCALES.find((l) => l.code === locale)?.native ?? locale;

  return (
    <List>
      {boxState.kind === 'loading' && (
        <SkeletonRows count={2} withLead={false} />
      )}
      {boxState.kind === 'loaded' && (
        <BoxPanel
          box={boxState.box}
          onBoxChange={(box) => setBoxState({ kind: 'loaded', box })}
        />
      )}
      {/* work-038: раньше при отказе `POST /boxes` блок ящика молча исчезал —
          человек видел Настройки без своего ящика и без единого слова почему.
          Своего ключа не заводим: `err_generic` уже есть. */}
      {boxState.kind === 'error' && <Placeholder description={t.err_generic} />}

      {/* work-020 (решение 7): свой код автора + ротация — только не-Premium
          (Premium получает кликабельную ссылку в сторис, код ему не нужен). */}
      {!isPremiumUser() && <MyCodePanel />}

      {/* work-036 (Трек D) — «Напиши мне»: панель шеринга message-ящика
          (лениво грузится InvitePanel'ом при раскрытии аккордеона — не на
          mount Настроек) + вход в «Полученные сообщения». */}
      <Section header={t.message_owner_entry}>
        <InvitePanel format="message" shareHint={t.message_share_hint} />
        <Cell onClick={onOpenMessages}>{t.message_inbox_open}</Cell>
      </Section>

      <Section header={t.settings_section_general}>
        {notifState.kind === 'loading' && <SkeletonRows count={1} withLead={false} />}
        {/* work-038: при неудачной загрузке переключателя НЕТ вообще.
            Раньше здесь стояло `notifState.kind === 'loaded' ? enabled : true` —
            тумблер рисовался включённым, хотя настоящее состояние неизвестно.
            Выключенный (или заблокированный) тумблер — та же ложь в другую
            сторону: серый переключатель читается как «уведомления выключены».
            Отсутствие переключателя — единственное честное представление
            неизвестного состояния. */}
        {notifState.kind === 'error' && (
          <Cell multiline description={t.settings_notifications_load_error}>
            {t.settings_notifications}
          </Cell>
        )}
        {notifState.kind === 'loaded' && (
          <Cell
            Component="label"
            after={
              <Switch
                checked={notifState.enabled}
                disabled={notifSaving}
                onChange={(e) => void handleNotificationsToggle(e.target.checked)}
              />
            }
            multiline
            description={
              notifError ?? t.settings_notifications_hint
            }
          >
            {t.settings_notifications}
          </Cell>
        )}
        {/* work-020: точка входа «Спросить по коду» (универсально, решение 6).
            Tappable-Cell кита даёт нативный press-feedback; chevron кит публично
            не экспортирует, поэтому навигацию несёт сам onClick. */}
        <Cell onClick={onEnterCode}>{t.settings_ask_by_code}</Cell>
        {/* retention-answer-loop: точка входа «Мои вопросы» — журнал вопросов,
            заданных с ЭТОГО устройства (localStorage), с ответами по мере готовности. */}
        <Cell onClick={onOpenMyQuestions}>{t.settings_my_questions}</Cell>
      </Section>

      {/* work-026: выбор языка из всех поддерживаемых (SUPPORTED_LOCALES).
          Список длинный (27) — прячем под аккордеон; в шапке слева подпись
          «Язык», справа — текущий выбранный (как нативный «Language» Telegram).
          Активный язык в списке помечаем галочкой. */}
      <Section>
        <Accordion expanded={langOpen} onChange={setLangOpen}>
          <Accordion.Summary className={styles.langHeader}>
            <span className={styles.langSummary}>
              <span>{t.settings_language}</span>
              <span className={styles.langCurrent}>{currentLocaleNative}</span>
            </span>
          </Accordion.Summary>
          <Accordion.Content>
            <div className={styles.langInner}>
              {localeError && <Cell description={localeError} />}
              {SUPPORTED_LOCALES.map((l) => (
                <Cell
                  key={l.code}
                  onClick={() => void handleLocaleChange(l.code)}
                  after={l.code === locale ? '✓' : undefined}
                  disabled={localeSaving}
                >
                  {l.native}
                </Cell>
              ))}
            </div>
          </Accordion.Content>
        </Accordion>
      </Section>

      {isAdmin && (
        <Section header="Аналитика">
          <Cell onClick={onOpenAnalytics}>Статистика посещаемости</Cell>
        </Section>
      )}

      <Section header={t.settings_section_about}>
        <Cell description={t.tagline}>{BRAND_NAME}</Cell>
        <Cell after={APP_VERSION}>{t.settings_version}</Cell>
      </Section>
    </List>
  );
}
